import { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import Image from 'app/components/Image';
import TableComponent from 'app/components/Table/TableComponent';
import { DEFAULT_TABLE_QUERY, QUERIES } from 'app/components/Table/core/constants';
import Typography from 'app/components/Typography';
import RootContentPage from 'app/components/layout/RootContentPage';

import { useChangeParams, useSyncQueryToState } from 'app/modules/query-state/hooks';
import VisualArchiveFilter from 'app/modules/system-settings/devices/components/VisualArchiveFilter';
import VisualArchiveFilterPopover from 'app/modules/system-settings/devices/components/VisualArchiveFilterPopover';
import { visualArchiveColumns } from 'app/modules/system-settings/devices/core/columns';
import { downloadZipAssets, getBinAssets } from 'app/modules/system-settings/devices/core/requests';
import {
  PreviewFile,
  VisualArchiveFilterParams,
} from 'app/modules/system-settings/devices/core/type';

import { usePageGoBack } from 'app/helpers/utils';
import { useBoolean } from 'app/hooks';

import { PATH } from 'app/constants/path';

import { ReactComponent as IconButton } from 'app/assets/icons/icon-export.svg';
import { ReactComponent as IconRedirect } from 'app/assets/icons/icon-redirect.svg';

import clsx from 'clsx';

const FilterButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const VisualArchive = () => {
  const goBack = usePageGoBack(PATH.SYSTEM_SETTING_DEVICE_SENSOR);
  const sensorName = useParams().name;
  const { onChangeParams } = useChangeParams();

  // Sync URL query parameters to state store with default params
  useSyncQueryToState(window.location.pathname, DEFAULT_TABLE_QUERY);

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [previewImage, setPreviewImage] = useState<PreviewFile | null>(null);
  const isShowFilter = useBoolean(false);
  const [dateFilter, setDateFilter] = useState<VisualArchiveFilterParams>({});
  const [refetchTimestamp, setRefetchTimestamp] = useState<number>(0);

  // Trigger refetch when sensorName changes
  useEffect(() => {
    setRefetchTimestamp(Date.now());
  }, [sensorName]);

  const onClickBodyTable = (row: any) => () => {
    setPreviewImage({
      url: row.original.url,
      type: row.original.type,
    });
  };

  const handleSelectItem = (itemId: string) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter((item) => item !== itemId));
      return;
    }

    setSelectedItems([...selectedItems, itemId]);
  };

  const handleBulkDownload = async () => {
    if (selectedItems.length === 0) return;
    await downloadZipAssets(sensorName ?? '', selectedItems);
  };

  const handleCloseModal = () => setPreviewImage(null);

  const handleGetBinAssets = (query: string) => getBinAssets(query, sensorName);

  const handleApplyFilter = (params: VisualArchiveFilterParams) => {
    setDateFilter(params);
    const filterParams: any = {};

    if (params.fromDate) {
      filterParams.fromDate = params.fromDate.toISOString();
    }
    if (params.toDate) {
      filterParams.toDate = params.toDate.toISOString();
    }

    // If params is empty (clear all), explicitly remove date filter parameters
    const isEmpty = Object.keys(filterParams).length === 0;
    onChangeParams(
      { ...filterParams, page: 1 },
      isEmpty ? { removeParams: ['fromDate', 'toDate'] } : undefined,
    );
    isShowFilter.setValue(false);
  };

  const handleClickFilter = (type: 'on' | 'off') => {
    isShowFilter.setValue(type === 'on');
  };

  return (
    <RootContentPage
      title=""
      header={
        <div className="d-flex items-center">
          <IconRedirect onClick={goBack} style={{ cursor: 'pointer' }} />
          <Typography variant="heading-6" className="mt-1">
            Sensor assets: {sensorName}
          </Typography>
        </div>
      }
    >
      <div className="position-relative">
        <TableComponent
          getList={handleGetBinAssets}
          nameQuery={QUERIES.VISUAL_ARCHIVE_LIST}
          originColumns={visualArchiveColumns(selectedItems, handleSelectItem)}
          onClickBodyTable={onClickBodyTable}
          isBorder={false}
          hideSearch={false}
          spacing="lg"
          isRowClickable
          placeholderSearch="Search by name, date (YYYY-MM-DD)"
          isAutoFetch={true}
          syncParamUrl={true}
          isShowPagination={true}
          refetchTimestamp={refetchTimestamp}
          isSortAndSearchFrontEnd={false}
          customListHeader={
            <FilterButtonWrapper>
              <Button
                onClick={handleBulkDownload}
                className={clsx('d-flex align-items-center justify-content-center', {
                  'opacity-0': selectedItems.length === 0,
                })}
                style={{
                  gap: 10,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: selectedItems.length === 0 ? 'none' : 'auto',
                  zIndex: 5,
                }}
              >
                <IconButton />
                Export
              </Button>
              <VisualArchiveFilterPopover
                visible={isShowFilter.value}
                onClickFilter={handleClickFilter}
                isFilter={!!dateFilter.fromDate || !!dateFilter.toDate}
                content={
                  isShowFilter.value && (
                    <VisualArchiveFilter
                      onApplyFilter={handleApplyFilter}
                      currentFilter={dateFilter}
                    />
                  )
                }
                extraClassBtnFilter="filter-button-visual-archive"
              />
            </FilterButtonWrapper>
          }
        />

        {previewImage && (
          <Modal centered show={!!previewImage} onHide={handleCloseModal} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>Preview</Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex justify-content-center overflow-x-hidden">
              {previewImage.type === 'image' ? (
                <Image
                  src={previewImage.url}
                  alt="Preview"
                  width="100%"
                  height={500}
                  className="m-0"
                />
              ) : (
                <video controls width="100%" height={500}>
                  <source src={previewImage.url} type="video/mp4" />
                </video>
              )}
            </Modal.Body>
          </Modal>
        )}
      </div>
    </RootContentPage>
  );
};

export default VisualArchive;
